/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import { ISource } from '@stores/source';
import { TAB_NAMES } from '@components/sources/source-view';
import DataWarehouseAuth from '@components/sources/source/warehouseSource/auth/dataWarehouseAuth';
import SchemaValidation from '@components/sources/source/warehouseSource/warehouseSettings/validateSource';
import { DIRECTORY_TABS } from '@components/common/constants';
import _ from 'lodash';
import { ISourceDataStore } from '@stores/sourceDataStore';
import { AccountValidator, CredentialsAuth, DirectoryTab, SourceCategory } from './sourceCategory';
import { SYNC_BEHAVIOUR_MODES } from '../visualizer/mappingScreens/visualMapping';
import Credentials from '../source-view/source-settings/components/credentials';
import SqlModelSettings from '../source-view/source-settings/components/sqlModelSettings';
import { IDestination } from '@stores/destination';

class WarehouseSource extends SourceCategory {
  override isNewConnectionAllowed(source: { destinations: unknown[] }) {
    return source.destinations.length === 0;
  }

  override isDestinationAllowedForConnection(destination: IDestination) {
    return destination.sources.length === 0;
  }

  override getSyncType(type?: string) {
    return type || '';
  }

  override async validateAccount({
    accountId,
    valid,
    accountInfo,
    source,
    setCredentials,
  }: AccountValidator) {
    if (valid) {
      const accountData = {
        accountId,
        accountInfo: valid ? accountInfo : undefined,
        role: source?.sourceDef.name,
      };
      const account = await source?.createAccount(accountData);
      if (account) {
        setCredentials({
          accountId: account.id,
          accountInfo: account.options,
          valid: true,
          updated: true,
        });
      }
      return account?.id;
    }
    return undefined;
  }

  override getAccountsEndpoint(sourceDef?: string) {
    const base = '/warehouseSources/accounts';
    if (sourceDef) {
      return `${base}/${sourceDef}`;
    }
    return base;
  }

  override getSourceType(_: { name: string }) {
    return 'warehouse';
  }

  override hideFromAllowedDestConnections() {
    return true;
  }

  override hasTransformationStep(sourceId: string, isDeviceMode: boolean) {
    return !sourceId && !isDeviceMode;
  }

  override hasWHMappingStep(selectedSource: unknown) {
    // TODO - return true in new workflow
    return !!selectedSource;
  }

  override isWarehouse() {
    return true;
  }

  override handleSetup(_: boolean, canCreateWarehouse: boolean, create: () => void) {
    if (canCreateWarehouse) {
      create();
      return true;
    }
    return false;
  }

  override isPartOf(tab: DirectoryTab) {
    return tab === DIRECTORY_TABS.WAREHOUSE_ACTIONS;
  }

  override getAuthComponent({
    handleChange,
    source,
    isValidationDisable,
    checkValid,
    isValidationLoading,
    credentials,
  }: CredentialsAuth) {
    return (
      <>
        <DataWarehouseAuth
          sourceDefinition={source?.sourceDef}
          onChange={handleChange}
          isValidationDisable={isValidationDisable}
          selectedAccount={
            credentials?.accountId !== undefined
              ? credentials?.accountId
              : source?.config?.rudderAccountId || source?.config?.accountId
          }
          checkValid={checkValid}
          isValidationLoading={isValidationLoading}
        />
        {!isValidationDisable && (
          <SchemaValidation
            status={isValidationLoading || 'hide'}
            checkValid={checkValid}
            sName={source?.sourceDef.name || ''}
            accountId={credentials?.accountId || ''}
            accountInfo={credentials?.accountInfo}
            sourceDisplayName={source?.sourceDef.displayName || ''}
          />
        )}
      </>
    );
  }

  override enableCredentialValidations() {
    return true;
  }

  override isReportsViewEnabled() {
    return true;
  }

  override isSyncDelayEnabled() {
    return true;
  }

  override getTabs(_: ISource) {
    return [TAB_NAMES.DESTINATIONS, TAB_NAMES.SYNCS, TAB_NAMES.SCHEMA, TAB_NAMES.SETTINGS];
  }

  override showSyncScheduleSettings() {
    return true;
  }

  override hasIncompatibleSyncMode(
    customerSyncBehaviours?: string[],
    srcSyncBehaviours?: string[],
    destSyncBehaviours?: string[],
  ): boolean {
    const srcCustSyncBehaviours = _.intersection(srcSyncBehaviours, customerSyncBehaviours);
    return !(destSyncBehaviours || [SYNC_BEHAVIOUR_MODES.UPSERT]).some(
      (destSyncMode: string) =>
        destSyncMode === SYNC_BEHAVIOUR_MODES.UPSERT ||
        srcCustSyncBehaviours.includes(destSyncMode),
    );
  }

  override getCredentialSettings(sourceDataStore: ISourceDataStore, isAdmin: boolean) {
    if (sourceDataStore.config.rudderAccountId || sourceDataStore.config.accountId) {
      return (
        <Credentials
          source={sourceDataStore}
          sourceDef={sourceDataStore?.sourceDef}
          config={sourceDataStore?.config}
          isAdmin={isAdmin}
        />
      );
    }
    return <SqlModelSettings sourceId={sourceDataStore.id} />;
  }
}

export default WarehouseSource;
